<template>
  <div
    ref="post"
    class="theme-bg-light"
    :style="`min-height: ${renderHeight}px`"
  >
    <div v-if="post && post.id && shouldRender">
      <!-- Top Section -->
      <div
        id="top-section"
        class="group/theme w-full theme-darker border-2"
      >
        <div class="theme-bg-darker theme-text-main relative">
          <div class="relative mx-auto flex flex-col lg:flex-row 2xl:justify-end min-[1700px]:max-w-screen-2xl">
            <!-- Top Left Content -->
            <div
              v-if="!hideTopLeft"
              class="relative hidden min-h-[200px] w-64 2xl:w-80 flex-col items-center justify-between gap-16 pl-standard pr-thin text-gray-200 xl:max-w-xs xl:flex"
            >
              <div class="flex h-full items-end overflow-hidden">
                <fa
                  :icon="['fal', species.toLowerCase()]"
                  class="text-[12rem] theme-text-icon -mb-6 mr-5 2xl:mr-0"
                />
              </div>
            </div>
            <!-- Top Center Content -->
            <div
              :class="{ 'group/theme-[:not(.theme-light)]:2xl:px-16': hideTopLeft }"
              class="theme-bg-dark flex w-full flex-1 flex-col py-standard px-standard 2xl:pb-10"
            >
              <vetster-editorial-top-center-content
                :post="post"
                :post-type="postType"
              />
            </div>

            <!-- Top Right Content -->
            <div
              v-if="!hideTopRight"
              class="relative w-full lg:max-w-xs py-standard px-standard lg:pl-4"
              :class="collapseTopRight ? 'flex' : 'hidden lg:flex'"
            >
              <vetster-editorial-top-right-content
                :post="post"
                :post-type="postType"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Sticky Nav (Table of Contents) -->
      <section class="sticky z-30 top-16 block lg:hidden shadow-lg min-h-[3.5rem]">
        <transition
          name="fade"
          appear
        >
          <vetster-dynamic-sticky-nav
            v-cloak
            :id="post.id"
            :categories="navHeaders"
            title-key="nav_title"
            position="top"
            :is-web-view="false"
          />
        </transition>
      </section>
      <!-- Bottom Section -->
      <div
        id="bottom-section"
        class="relative mx-auto flex flex-1 min-[1700px]:max-w-screen-2xl"
      >
        <div class="sticky top-16 z-30 hidden h-full shrink-0 lg:block overflow-hidden">
          <vetster-left-sticky-collapsable-content
            :categories="navHeaders"
            :post="post"
            class="py-standard"
          />
        </div>

        <!--
          The class "min-w-0" was added to the div to fix a bug with Chart.js when the chart is resized.
          The issue happens when the width is reduced and the chart is not resized properly.
          https://medium.com/@jamiecarter7/chartjs-responsive-resize-bug-fix-1806181d85ce
          This will be needed if we start adding charts inside the content.
        -->
        <div class="bg-white flex-1 min-w-0">
          <div class="py-thick px-standard group/theme-[:not(.theme-light)]:2xl:px-16">
            <vetster-post-cover
              v-if="post.cover"
              :post="post"
            />
            <div v-if="post?.key_takeaway?.html">
              <vetster-post-nav-header
                nav-title="Key takeaways"
                title="Key takeaways"
                :append-id="`-${post.id}`"
              />
              <vetster-post-content
                :html="post.key_takeaway.html"
                class="mt-4 lg:mt-6 mb-1"
              />
            </div>
            <component
              :is="resolveComponentName(component.component)"
              v-for="(component, i) in post.components"
              :key="component.id + Math.random()"
              :class="resolveComponentPadding(component.component, i)"
              v-bind="resolveComponentProps(component)"
            />
            <vetster-post-nav-header
              nav-title="Editorial committee"
              title="Our editorial committee"
              title-description="Our medical review team is responsible for validating and maintaining the quality of our medical information."
              :append-id="`-${post.id}`"
              class="mt-14 xl:mt-16"
            />
            <vetster-editorial-committee-team
              :editorial-team="editorialTeam"
              :has-webview="hasWebview"
            />
            <div class="lg:hidden flex flex-col items-start mt-12">
              <vetster-editorial-module
                :post="post"
                :mobile-apps="mobileApps"
              />
            </div>
          </div>
        </div>

        <div class="sticky top-16 z-30 hidden h-full max-w-xs shrink-0 overflow-y-auto overflow-x-hidden lg:block">
          <div class="w-80 py-standard pr-standard pl-thin group/theme-[:not(.theme-light)]:border-l-2 group/theme-[:not(.theme-light)]:border-ocean-100 min-h-[450px]">
            <vetster-editorial-module
              :post="post"
              :mobile-apps="mobileApps"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VetsterEditorialModule from '@/brand-components/Content/PetHealth/VetsterEditorialModule';
import VetsterEditorialTopCenterContent from '@/brand-components/Content/PetHealth/VetsterEditorialTopCenterContent';
import VetsterEditorialTopRightContent from '@/brand-components/Content/PetHealth/VetsterEditorialTopRightContent';
import VetsterEditorialCommitteeTeam from '@/brand-components/Content/VetsterEditorialCommitteeTeam';
import VetsterLeftStickyCollapsableContent from '@/brand-components/Content/VetsterLeftStickyCollapsableContent';
import VetsterPostContent from '@/brand-components/Content/VetsterPostContent';
import VetsterPostCover from '@/brand-components/Content/VetsterPostCover';
import VetsterPostEducation from '@/brand-components/Content/VetsterPostEducation';
import VetsterPostNavHeader from '@/brand-components/Content/VetsterPostNavHeader';
import VetsterReference from '@/brand-components/Content/VetsterReference';
import VetsterShoutout from '@/brand-components/Content/VetsterShoutout';
import VetsterTable from '@/brand-components/Content/VetsterTable';
import VetsterVetPartner from '@/brand-components/Content/VetsterVetPartner';
import VetsterDynamicStickyNav from '@/components/VetsterDynamicStickyNav';
import PostComponentType from '@/enums/PostComponentType';
import NextPageScrolling from '@/mixins/NextPageScrolling';

export default {
  name: 'VetsterPetHealthPost',

  components: {
    VetsterDynamicStickyNav,
    VetsterEditorialCommitteeTeam,
    VetsterEditorialModule,
    VetsterEditorialTopCenterContent,
    VetsterEditorialTopRightContent,
    VetsterLeftStickyCollapsableContent,
    VetsterPostContent,
    VetsterPostCover,
    VetsterPostEducation,
    VetsterPostNavHeader,
    VetsterReference,
    VetsterShoutout,
    VetsterTable,
    VetsterVetPartner,
  },

  mixins: [NextPageScrolling],

  props: {
    collapseTopRight: {
      type: Boolean,
      default: false,
    },
    editorialTeam: {
      type: Array,
      default: () => [],
    },
    hasWebview: {
      type: Boolean,
      default: false,
    },
    hideTopLeft: {
      type: Boolean,
      default: false,
    },
    hideTopRight: {
      type: Boolean,
      default: false,
    },
    mobileApps: {
      type: Object,
      required: true,
    },
    post: {
      type: Object,
      default: () => ({}),
    },
    postType: {
      type: String,
      required: true,
    },
    species: {
      type: String,
      required: true,
    },
  },

  computed: {
    navHeaders() {
      const navHeaders = this.post.nav_headers || [];

      if (this.post.key_takeaway?.html) {
        navHeaders.unshift({
          id: Math.random().toString(36).substring(2, 15),
          component: PostComponentType.PostNavHeader,
          nav_title: 'Key takeaways',
          title: 'Key takeaways',
          title_description: null,
        });
      }

      const hasEditorialCommittee = navHeaders.some((header) => header.nav_title === 'Editorial committee');

      if (!hasEditorialCommittee) {
        navHeaders.push({
          id: Math.random().toString(36).substring(2, 15),
          component: PostComponentType.PostNavHeader,
          nav_title: 'Editorial committee',
          title: 'Our editorial committee',
          title_description: 'Our medical review team is responsible for validating and maintaining the quality of our medical information.',
        });
      }

      return navHeaders.map((navHeader) => ({
        ...navHeader,
        slug: `${navHeader.slug}-${this.post.id}`,
      }));
    },
  },

  watch: {
    shouldRender() {
      if (this.postObserver) {
        this.postObserver.disconnect();
        this.observePost(this.$refs.post, this.post);
      }
    },
  },

  mounted() {
    this.observePost(this.$refs.post, this.post);
  },

  methods: {
    resolveComponentName(name) {
      switch (name) {
        case PostComponentType.PostContent:
        case PostComponentType.PostVideo:
          return 'vetster-post-content';
        case PostComponentType.PostEducation:
          return 'vetster-post-education';
        case PostComponentType.PostNavHeader:
          return 'vetster-post-nav-header';
        case PostComponentType.PostShoutout:
          return 'vetster-shoutout';
        case PostComponentType.PostTable:
          return 'vetster-table';
        case PostComponentType.PostReference:
          return 'vetster-reference';
        case PostComponentType.PostVetPartner:
          return 'vetster-vet-partner';
        default:
          return null;
      }
    },
    resolveComponentPadding(name, i) {
      if (i === 0 && this.post.cover) {
        return '';
      }

      return name === PostComponentType.PostNavHeader
        ? 'mt-14 xl:mt-16'
        : 'mt-6';
    },
    resolveComponentProps(component) {
      switch (component.component) {
        case PostComponentType.PostContent:
        case PostComponentType.PostVideo:
          return { html: component.html };
        case PostComponentType.PostEducation:
          return {
            concern: component.concern,
            concernLabel: component.concern_label,
            mobileApps: this.mobileApps,
            title: component.title,
          };
        case PostComponentType.PostNavHeader:
          return {
            navTitle: component.nav_title,
            appendId: `-${this.post?.id}`,
            title: component.title,
            titleDescription: component.title_description,
          };
        case PostComponentType.PostShoutout:
          return {
            description: component.description,
            title: component.title,
            url: component.url,
          };
        case PostComponentType.PostTable:
          return {
            items: component.table,
            title: component.table_title,
          };
        case PostComponentType.PostReference:
          return {
            articleAgency: component.article_agency,
            articleName: component.article_name,
            articlePublication: component.article_publication,
            articleUrl: component.article_url,
            authorCredit: component.author_credit,
            description: component.description,
          };
        case PostComponentType.PostVetPartner:
          return {
            searchType: this.postType.toUpperCase(),
            species: [this.post?.species_type].filter(Boolean),
          };
        default:
          return { component };
      }
    },
  },
};
</script>
